import React, { useState, useEffect } from "react";
import { X, CheckCircle, AlertCircle, Brain } from "lucide-react";
import { api, API_BASE } from "./api_service/api";

interface ImageResultModalProps {
  result: any;
  onClose: () => void;
}

const ImageResultModal: React.FC<ImageResultModalProps> = ({
  result,
  onClose,
}) => {
  const [explanation, setExplanation] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isReal = result.prediction === "Real" || result.source === "human";
  const aiPercent = result.ai_percentage?.toFixed(1) || "0.0";
  const humanPercent = result.human_percentage?.toFixed(1) || "0.0";
  const confidence = (result.confidence || 0).toFixed(1);
  const duration = result.duration;

  useEffect(() => {
    if (!result?.explanation) return;

    let abortController = new AbortController();

    const startReasoning = async () => {
      setIsLoading(true);
      setError(null);
      setExplanation("");

      try {
        // Force re-authentication
        if (!api.isAuthenticated) {
          console.log("Authenticating...");
          await api.login();
        }

        // Wait a bit to ensure auth is complete
        await new Promise((resolve) => setTimeout(resolve, 100));

        const apiKey = (api as any).apiKey;
        const token = (api as any).token;

        console.log("Auth check:", {
          hasApiKey: !!apiKey,
          hasToken: !!token,
          isAuthenticated: api.isAuthenticated,
        });

        if (!apiKey || !token) {
          throw new Error("Authentication failed - missing credentials");
        }

        console.log("Starting image stream...");

        const payload = {
          ...result.explanation,
          confidence: parseFloat(result.explanation.confidence),
        };

        console.log("Payload:", payload);

        const response = await fetch(`${API_BASE}/reasoning/stream-image`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-api-key": apiKey,
            Authorization: `Bearer ${token}`,
            Accept: "text/event-stream",
          },
          body: JSON.stringify(payload),
          signal: abortController.signal,
        });

        console.log("Response status:", response.status);
        console.log(
          "Response headers:",
          Object.fromEntries(response.headers.entries())
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Error response:", errorText);
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }

        if (!response.body) {
          throw new Error("No response body");
        }

        console.log("Stream started");

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            console.log("Stream complete");
            setIsLoading(false);
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (!line.trim() || !line.startsWith("data: ")) continue;

            try {
              const data = JSON.parse(line.substring(6));
              console.log("Received:", data.type, data);

              if (data.type === "start") {
                setExplanation(data.content || "");
              } else if (data.type === "chunk") {
                setExplanation((prev) => prev + (data.content || ""));
              } else if (data.type === "done") {
                setIsLoading(false);
                return;
              } else if (data.type === "error") {
                console.error("Stream error:", data);
                setError(data.content || "An error occurred");
                setIsLoading(false);
                return;
              }
            } catch (e) {
              console.error("Parse error:", e, line);
            }
          }
        }
      } catch (err: any) {
        if (err.name === "AbortError") {
          console.log("Stream aborted");
          return;
        }
        console.error("Error:", err);
        setError(err.message || "Failed to generate reasoning");
        setIsLoading(false);
      }
    };

    startReasoning();

    return () => {
      console.log("Aborting stream");
      abortController.abort();
    };
  }, [result?.explanation]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
      <div className="relative w-full max-w-4xl bg-white rounded-3xl shadow-3xl overflow-hidden animate-in fade-in zoom-in duration-300 max-h-[95vh] overflow-y-auto">
        <div
          className={`p-10 text-center text-white ${
            isReal
              ? "bg-gradient-to-br from-emerald-500 to-teal-600"
              : "bg-gradient-to-br from-rose-500 to-pink-600"
          }`}
        >
          {isReal ? (
            <CheckCircle size={90} className="mx-auto mb-4 drop-shadow-lg" />
          ) : (
            <AlertCircle size={90} className="mx-auto mb-4 drop-shadow-lg" />
          )}
          <h2 className="text-5xl font-black">
            {result.prediction === "Real" ? "REAL" : "AI GENERATED"}
          </h2>
          <p className="text-xl mt-3 opacity-90">Image Authenticity Result</p>
        </div>

        {/* Body */}
        <div className="p-8 space-y-8 bg-gradient-to-b from-gray-50 to-white">
          {/* AI vs Human Progress Bar */}
          <div className="space-y-4">
            <div className="flex justify-between text-sm font-semibold text-gray-600">
              <span>AI Generated</span>
              <span>Real Photo</span>
            </div>
            <div className="relative h-20 bg-gray-200 rounded-2xl overflow-hidden shadow-inner">
              <div
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-rose-500 to-pink-500 transition-all duration-1000"
                style={{ width: `${aiPercent}%` }}
              />
              <div
                className="absolute right-0 top-0 h-full bg-gradient-to-l from-emerald-500 to-teal-500 transition-all duration-1000"
                style={{ width: `${humanPercent}%` }}
              />
              <div className="absolute inset-0 flex items-center justify-center text-3xl font-black text-white drop-shadow-2xl">
                {aiPercent}% AI • {humanPercent}% REAL
              </div>
            </div>
          </div>

          {/* Confidence + Duration */}
          <div className="grid grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-6 text-center border border-gray-200 shadow">
              <p className="text-gray-600 text-sm font-medium">Confidence</p>
              <p className="text-5xl font-bold text-indigo-700 mt-2">
                {confidence}%
              </p>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center border border-gray-200 shadow">
              <p className="text-gray-600 text-sm font-medium">
                Processing Time
              </p>
              <p className="text-5xl font-bold text-purple-700 mt-2">
                {duration}s
              </p>
            </div>
          </div>

          {/* Detailed Reasoning */}
          <div className="p-6 rounded-2xl border border-gray-200 bg-white">
            <div className="flex items-center gap-3 mb-6">
              <Brain className="w-6 h-6 text-indigo-600" />
              <h4 className="text-lg font-semibold">Detailed Reasoning</h4>
              {isLoading && (
                <div className="ml-auto">
                  <div className="animate-spin h-5 w-5 border-2 border-indigo-600 border-t-transparent rounded-full" />
                </div>
              )}
            </div>
            <div className="min-h-[100px]">
              {error ? (
                <div className="text-red-600">
                  <p className="font-semibold mb-2">Error:</p>
                  <p>{error}</p>
                </div>
              ) : explanation ? (
                <pre className="whitespace-pre-wrap font-sans text-gray-700">
                  {explanation}
                </pre>
              ) : (
                <span className="text-gray-500 italic">
                  {isLoading
                    ? "Connecting and generating reasoning..."
                    : "No reasoning available"}
                </span>
              )}
            </div>
          </div>

          {/* Analyzed At */}
          <p className="text-center text-gray-500 text-sm">
            Analyzed on{" "}
            {new Date(result.analysed_at || Date.now()).toLocaleString()}
          </p>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-3 rounded-full bg-white/20 hover:bg-white/40 backdrop-blur transition"
        >
          <X size={28} className="text-white" />
        </button>
      </div>
    </div>
  );
};

export default ImageResultModal;
